/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ImageWithPreload, ToggleTab, ToggleTabContext, ToggleTabPanel, ToggleTabs } from '@coursera-web/cds-labs';
import type { ToggleTabValue } from '@coursera-web/cds-labs';

import { Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { EntWebsiteHeroContent } from 'bundles/ent-website/__generated__/baseContentfulTypes';
import type {
  EntWebsiteBlockHeroQuery,
  EntWebsiteHeroFragment,
  EntWebsiteProofPointFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
import BlockLayout from 'bundles/ent-website/contentful/core/BlockLayout';
import ContentBody from 'bundles/ent-website/contentful/modules/EntWebsiteHero/ContentBody';
import ProofPoints from 'bundles/ent-website/contentful/modules/ProofPoints';
import { isInvertedTheme } from 'bundles/ent-website/hooks/useBlockLayoutTheme';
import { entWebsiteBlockHeroQuery } from 'bundles/ent-website/queries/EntWebsiteHero';
import type { NonNullableArray } from 'bundles/ent-website/types';
import { eventingTransform } from 'bundles/ent-website/utils/eventingTransform';
import withBlockQuery, { type BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';

import _t from 'i18n!nls/ent-website';

const styles = {
  eyebrowTitle: css`
    color: inherit;
  `,
  title: css`
    color: inherit;
    margin: var(--cds-spacing-150) 0;
  `,
  toggleTab: css`
    margin: var(--cds-spacing-300) 0;
  `,
  heroImageWrapper: css`
    width: 100%;
    height: 100%;
  `,
  heroImage: css`
    max-height: 100% !important;
    object-fit: cover;
    object-position: top;
    height: 100% !important;
    width: 100%;
  `,
};

export function HeroModule({ content, assembly = {} }: BlockModuleInnerProps<EntWebsiteHeroFragment>) {
  const {
    primaryHeroContent,
    secondaryHeroContent,
    eyebrow,
    headline,
    selectedTab: initialSelectedTab,
    proofPointsCollection,
    image,
  } = content;

  const track = useTracker();
  const trackingName = eventingTransform(`${content.__typename}_${content.slug}_toggle_tab_0`);
  const [selectedTab, setSelectedTab] = React.useState<ToggleTabValue>(
    initialSelectedTab === 'primaryHeroContent' ? 'primary' : 'secondary'
  );

  const isInverted = isInvertedTheme(assembly.theme ?? '');

  const handleTabChange = (selected: ToggleTabValue) => {
    setSelectedTab(selected);
    // Track tab click with the actual tab label
    const selectedTabLabel =
      selected === 'primary'
        ? primaryHeroContent?.tabLabel || _t('Option 1')
        : secondaryHeroContent?.tabLabel || _t('Option 2');

    track('click_tab', {
      tab: {
        name: trackingName,
        selectedTab: selectedTabLabel,
      },
    });
  };

  const isDualTabLayout = !!content.secondaryHeroContent;

  const proofPointsToShow = proofPointsCollection?.items
    ? (proofPointsCollection.items.filter((pp) => !!pp) as NonNullableArray<EntWebsiteProofPointFragment>)
    : [];

  const contentPanel = () => {
    return (
      <div id="hero-content">
        {eyebrow && (
          <Typography2 variant="subtitleMedium" css={styles.eyebrowTitle} component="p">
            {eyebrow}
          </Typography2>
        )}
        <Typography2 variant="titleLarge" css={styles.title} component="h1">
          {headline}
        </Typography2>

        {isDualTabLayout ? (
          <ToggleTabContext value={selectedTab} invert={isInverted} onChange={handleTabChange}>
            <ToggleTabs aria-label={_t('Hero Tab Toggle')}>
              <ToggleTab value="primary" label={primaryHeroContent?.tabLabel || _t('Option 1')} />
              <ToggleTab value="secondary" label={secondaryHeroContent?.tabLabel || _t('Option 2')} />
            </ToggleTabs>
            <ToggleTabPanel value="primary">
              <ContentBody tabContent={primaryHeroContent as EntWebsiteHeroContent} module={content} />
            </ToggleTabPanel>
            <ToggleTabPanel value="secondary">
              <ContentBody tabContent={secondaryHeroContent as EntWebsiteHeroContent} module={content} />
            </ToggleTabPanel>
          </ToggleTabContext>
        ) : (
          <ContentBody tabContent={primaryHeroContent as EntWebsiteHeroContent} module={content} />
        )}
      </div>
    );
  };

  const imagePanel = () =>
    image ? (
      <div css={styles.heroImageWrapper}>
        <ImageWithPreload
          shouldPreload={true}
          css={styles.heroImage}
          url={image.url}
          description={image.description}
          width={image.width ?? undefined}
        />
      </div>
    ) : null;

  return (
    <React.Fragment>
      <BlockLayout
        assembly={{ anchorId: 'hero-module', layout: 'hero', theme: assembly.theme }}
        renderContent={contentPanel}
        renderImage={imagePanel}
      />
      {proofPointsToShow.length && <ProofPoints proofPoints={proofPointsToShow} />}
    </React.Fragment>
  );
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockHeroModule = withBlockQuery<EntWebsiteHeroFragment, EntWebsiteBlockHeroQuery>(
  entWebsiteBlockHeroQuery
)(HeroModule);
